import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { Chess } from '@chess/api-chess';
import { CHESS_API, uciToInput } from '@chess/api-chess';
import { REVIEW_API, type ReviewSession, type ReviewState } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { openingMark } from './head.ts';
import { headOf, stageBadges, stageTitle, stageTitleTooltip } from './header-view.ts';
import { StageHeader } from './StageHeader.tsx';
import './header.css';
import { type Fen, type Uci, type Phase } from '@chess/api-model';

// The generic head of a stage tab, built once and rendered by every stage
// through the `review.stageHeader` extension with `{ phase }`: the title
// (the opening's name for the opening, the stage's name otherwise), the
// stage's moves, the score when it began and at the reader's first move
// after it with the swing from the reader's side, both as links to those
// plies, and the reader's blunders and misses in the stage as two counts. Honest while the scan runs and
// when the game ended before the stage. Each stage's score (the change across
// it, from the reader's side) is shown on the stage's tab, not in the body:
// this plugin publishes it with `layout.tabBadge` for all three tabs at once,
// whichever tab is open.

interface HeaderParams {
  phase: Phase;
}

export function setup(app: PiletApi) {
  let chess: Chess | undefined;
  void app.getApi<Chess>(CHESS_API).then((c) => (chess = c));
  const toSan = (fen: Fen, uci: Uci): string | undefined => chess?.play(fen, uciToInput(uci))?.move.san;

  const follow = async () => {
    // The game, the reader, the scan and the book's reading are the review
    // session's, loaded once per game for every panel.
    const review = await app.getApi<ReviewSession>(REVIEW_API);

    // The view is `StageHeader`, a presentational component shared with the
    // plugin's Storybook; this wrapper feeds it from the store and the review.
    const Header: React.FC<{ params: HeaderParams }> = ({ params }) => {
      const s = useSelect(review, (state) => state) ?? review.state();
      const { phase } = params;
      const game = s.game;
      const reading = s.book?.reading;
      const title = stageTitle(phase, reading);
      const titleTooltip = stageTitleTooltip(phase, reading);
      const head = headOf(s, phase, toSan);
      const mark = phase === 'opening' && game ? openingMark(game, s.scan?.scan, s.reader, s.scan?.rules) : undefined;
      return <StageHeader phase={phase} title={title} titleTooltip={titleTooltip} game={game} head={head} reader={s.reader} scanning={s.scanning} mark={mark} onGo={(ply) => review.go(ply)} />;
    };

    app.registerExtension<HeaderParams>('review.stageHeader', Header);

    // Each stage's score on its tab, for every stage whether or not its tab is
    // open, recomputed whenever the game, the scan or the reader changes.
    let last = '';
    const publishScores = (s: ReviewState) => {
      const badges = stageBadges(s, toSan);
      const key = JSON.stringify(badges);
      if (key === last) return;
      last = key;
      for (const b of badges) app.emit('layout.tabBadge', { slot: 'review.sidePanel', id: b.phase, badge: b.text, tone: b.tone });
    };
    review.subscribe(publishScores);
    publishScores(review.state());
  };
  void follow();
}
